import { ProductCodeKey, UserInfo } from '@dayetopia/types';
import * as Sentry from '@sentry/react';
import { Auth, User, UserCredential, createUserWithEmailAndPassword, signInAnonymously } from 'firebase/auth';
import getOmsUrl from './getOmsUrl';

export function getProductIdFromPlanCode(planCode?: string | null) {
  if (planCode?.includes('_bundle')) {
    return planCode;
  }
  if (planCode?.includes('ppc_tampon_')) {
    return ProductCodeKey.PPCTamponBox;
  }
  if (planCode?.includes('tampon_')) {
    return ProductCodeKey.TamponBox;
  }
  if (planCode?.includes('proviotics')) {
    return ProductCodeKey.Proviotics;
  }
  if (planCode?.includes('contraception')) {
    return 'contraception_box';
  }
  if (planCode?.includes('screening_kit')) {
    return 'screening_kit';
  }
  if (planCode?.includes('pads')) {
    return 'pads_box';
  }
  return null;
}

export function getSubscriptionTitle(productId: string | undefined | null, full: boolean) {
  const subText = full ? ' subscription' : '';
  if (productId === 'tampon_box') {
    return `Tampons${subText}`;
  }
  if (productId === 'proviotics') {
    return `ProViotics${subText}`;
  }
  if (productId === 'contraception_box') {
    return `Contraception${subText}`;
  }
  if (productId === 'screening_kit') {
    return `Vaginal Screening`;
  }
  if (productId === 'pads_box') {
    return `Pads${subText}`;
  }
  if (productId === 'ndt_box') {
    return `NDT${subText}`;
  }
  if (productId === 'ppc_tampon_box') {
    return `Prescription CBD tampons${subText}`;
  }
  if (productId === 'tampon_box_bundle_heavy_flow_mm') {
    return `Heavy Flow Bundle`;
  }
  if (productId === 'tampon_box_bundle_regular_flow_mm') {
    return `Regular Flow Bundle`;
  }
  return '';
}

export function convertVariantsToAddOns(boxVariants) {
  return Object.keys(boxVariants)
    .filter((key) => boxVariants[key] > 0 && key !== 'boxSize')
    .map((key) => ({
      quantity: boxVariants[key],
      unitAmount: 0,
      code: key
    }));
}

export async function createRecurlyAccountWithGoogleId(user: User, captchaToken?: string): Promise<void> {
  try {
    const omsUrl = getOmsUrl() ?? '';
    const formattedEmail = user.email;
    const body = { email: formattedEmail, captchaToken };

    const googleIdToken = await user.getIdToken();
    const response = await fetch(`${omsUrl}/recurly/create-account`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/html',
        token: googleIdToken
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error('Failed to create Recurly account');
    }
  } catch (err) {
    Sentry.captureException(err, { tags: { failed: 'createRecurlyAccountWithGoogleId' } });
    throw err;
  }
}

export function createGoogleIdentityAccount(
  firebaseAuth: Auth | undefined,
  email: string,
  password: string,
  emailAlreadyUsedCallback: () => void
): Promise<User> {
  return new Promise((resolve, reject) => {
    const formattedEmail = email.trim().toLowerCase();
    if (!firebaseAuth) return;

    createUserWithEmailAndPassword(firebaseAuth, formattedEmail, password)
      .then((userCredential: UserCredential) => {
        const { user } = userCredential;
        resolve(user);
      })
      .catch((err) => {
        if (err.code === 'auth/email-already-in-use') {
          emailAlreadyUsedCallback();
        }
        Sentry.captureException(err, { tags: { failed: 'createGoogleIdentityAccount' } });
        reject(err);
      });
  });
}

export function signInAsGuest(firebaseAuth: Auth | undefined): Promise<User | undefined> {
  return new Promise((resolve, reject) => {
    if (!firebaseAuth) return;
    signInAnonymously(firebaseAuth)
      .then((userCredential: UserCredential) => {
        const { user } = userCredential;
        resolve(user);
      })
      .catch((err) => {
        Sentry.captureException(err, { tags: { failed: 'guestSignIn' } });
        reject(err);
      });
  });
}

export function personalIdDetailsChanged(userInfo: UserInfo, newFirstName: string, newLastName: string, newDateOfBirth: string) {
  const { firstName, lastName, dateOfBirth } = userInfo;

  if ((firstName || '') !== (newFirstName || '')) {
    return true;
  }

  if ((lastName || '') !== (newLastName || '')) {
    return true;
  }

  if ((dateOfBirth || '') !== (newDateOfBirth || '')) {
    return true;
  }

  return false;
}
